import React from 'react';
import PropTypes from 'prop-types';
import TextInput from '../Global/Forms/TextInput';
import TextareaInput from '../Global/Forms/TextareaInput';
import SelectInput from '../Global/Forms/SelectInput';
import CheckboxButton from '../Global/Forms/CheckboxButton';
import RadioButton from '../Global/Forms/RadioButton';
import ManageContentFieldGroup from './ManageContentFieldGroup';
import MaxWidthContainer from '../Global/Containers/MaxWidthContainer';
import TooltipWithDropdown from '../Global/Tooltips/TooltipWithDropdown';
import ManageContentSelectInputType from './ManageContentSelectInputType';
import FreeFormButtons from '../Global/Forms/FreeFormButtons';
import SortableComponent from '../Global/Sortable/SortableComponent';
import ImageUploadOrDiagramSelect from '../Global/Forms/ImageUploadOrDiagramSelect';

const ManageContentForm = ({ content, metadataOptions, onChange, onDiagramChange, errors, addInput, addTextarea, addListItem, addAccordionRow, deleteInput, onSortEnd, getContentPage }) => {
  const ageOptions = [];
  const type = getContentPage();
  for (let i = 4; i <= 21; i++) {
    ageOptions.push({ value: i, label: i.toString() });
  }

  const lengthOfPracticeOptions = [];
  for (let i = 5; i <= 30; i++) {
    const option = `${i.toString()} minutes`;
    lengthOfPracticeOptions.push({ value: i, label: option });
  }

  const numberOfPlayersOptions = [];
  for (let i = 1; i <= 22; i++) {
    numberOfPlayersOptions.push({ value: i, label: i.toString() });
  }

  const numberOfBallsConesOptions = [];
  for (let i = 1; i <= 30; i++) {
    numberOfBallsConesOptions.push({ value: i, label: i.toString() });
  }

  const mainContentFields = [];
  if (content.main_content) {
    content.main_content.map((object, contentIndex) => (
      mainContentFields.push(
        <ManageContentSelectInputType
          id={object.id}
          content={object}
          contentIndex={contentIndex}
          deleteInput={deleteInput}
          addTextarea={addTextarea}
          addListItem={addListItem}
          addAccordionRow={addAccordionRow}
          onChange={onChange}
          onDiagramChange={onDiagramChange}
        />,
      )
    ));
  }

  const inputContainerClass = `input-with-tooltip`;
  if (content.type === `document` || type === `document`) {
    return (
      <div className="drill-creation-form-fields">

        <div className="drill-creation-section">
          <MaxWidthContainer>
            <div className={inputContainerClass}>
              <TextareaInput
                name="title"
                label="Title*"
                maxLength={200}
                value={content.title}
                placeholder="E.g. Coach Standards: What We Expect"
                onChange={onChange}
                error={errors.title}
                autoResize
              >
                <TooltipWithDropdown
                  tooltipText="
                    What is the title of your document? A good title should be short and descriptive.<br/><br/>
                    An example of a good title would be: <i>Coach Standards: a brief introduction.</i>.
                  "
                />
              </TextareaInput>
            </div>

            <div className={inputContainerClass}>
              <TextareaInput
                name="summary"
                label="Summary"
                maxLength={1000}
                value={content.summary}
                placeholder="Briefly summarise your document in one sentence. Any additional details can be added further down."
                onChange={onChange}
                error={errors.summary}
                autoResize
              >
                <TooltipWithDropdown
                  tooltipText="
                    A brief explanation about what this document is about.<br/><br/>
                    An example of a good summary would be: <i>This document aims to provide details on coach standards</i>.
                  "
                />
              </TextareaInput>
            </div>

            <div className={inputContainerClass}>
              <span className="faux-label">Main Image</span>
              <TooltipWithDropdown tooltipText="A good quality image can improve the look and feel of a document." />
              {content.image && `data` in content.image &&
                <ImageUploadOrDiagramSelect image={content.image.data} onImageChange={onChange} onDiagramChange={onDiagramChange} />
              }
            </div>
          </MaxWidthContainer>
        </div>

        <div className="drill-creation-section">
          <MaxWidthContainer>
            <div className="heading-with-tooltip">
              <h2 className="heading">Document Information</h2>
              <TooltipWithDropdown tooltipText="Any other information you'd like to add should be inserted here. Use the buttons below to add headings, paragraphs, numbered lists, bullet lists, dropdowns, images and videos." />
              <p className="text-grey">Click and drag the handle icon to re-order content or click the delete button to remove</p>
            </div>

            <div className="main-content-fields">
              <SortableComponent items={mainContentFields} onSortEnd={onSortEnd} />
              <FreeFormButtons addInput={addInput} />
            </div>
          </MaxWidthContainer>
        </div>

      </div>
    );
  }
  return (
    <div className="drill-creation-form-fields">

      <div className="drill-creation-section">
        <MaxWidthContainer>
          <div className={inputContainerClass}>
            <TextareaInput
              name="title"
              label="Title*"
              maxLength={200}
              value={content.title}
              placeholder="E.g. Combination Play: Combinations to Create Chances"
              onChange={onChange}
              error={errors.title}
              autoResize
            >
              <TooltipWithDropdown
                tooltipText="
                    What is the title of your practice? A good title should be short and descriptive.<br/><br/>
                    An example of a good title would be: <i>Combination Play: Combinations to Create Chances</i>.
                  "
              />
            </TextareaInput>
          </div>

          <div className={inputContainerClass}>
            <TextareaInput
              name="summary"
              label="Summary"
              maxLength={1000}
              value={content.summary}
              placeholder="Briefly summarise your practice in one sentence. Any additional details can be added further down."
              onChange={onChange}
              error={errors.summary}
              autoResize
            >
              <TooltipWithDropdown
                tooltipText="
                    A brief explanation about what the objectives of this practice.<br/><br/>
                    An example of a good summary would be: <i>This practice will give players opportunities to beat a defender and create a goal-scoring chance</i>.
                  "
              />
            </TextareaInput>
          </div>

          <div className={inputContainerClass}>
            <span className="faux-label">Main Diagram Image</span>
            <TooltipWithDropdown tooltipText="A good quality practice diagram will make it easier for other people to replicate your practice." />
            {content.image && `data` in content.image &&
            <ImageUploadOrDiagramSelect image={content.image.data} onImageChange={onChange} onDiagramChange={onDiagramChange} />
              }
          </div>

          <div className={inputContainerClass}>
            <ManageContentFieldGroup
              name="age"
              label="Ages"
              value={content.age}
              defaultOption="Select Ages"
              options={ageOptions}
              onChange={onChange}
              error={errors.age}
              component={CheckboxButton}
            >
              <TooltipWithDropdown tooltipText="The age group that the practice is appropriate for." />
            </ManageContentFieldGroup>
          </div>
        </MaxWidthContainer>
      </div>

      <div className="drill-creation-section">
        <MaxWidthContainer>
          <div className="heading-with-tooltip">
            <h2 className="heading">Tagging</h2>
            <TooltipWithDropdown tooltipText="Tagging your practice will make it easier for you to find your practice in the future and also give other coaches an overview of the key aspects of the practice." />
          </div>

          <div className={inputContainerClass}>
            <ManageContentFieldGroup
              name="phase_of_the_game"
              label="Phase of the Game (select one)"
              value={content.phase_of_the_game}
              defaultOption="Select Phase of the Game"
              options={metadataOptions.phaseofthegame}
              onChange={onChange}
              error={errors.phase_of_the_game}
              component={RadioButton}
            >
              <TooltipWithDropdown tooltipText="The Phase of the Game where the practice will be focusing." />
            </ManageContentFieldGroup>
          </div>

          <div className={inputContainerClass}>
            <ManageContentFieldGroup
              name="theme"
              label="Themes"
              value={content.theme}
              defaultOption="Select Themes"
              options={metadataOptions.theme}
              onChange={onChange}
              error={errors.theme}
              component={CheckboxButton}
            >
              <TooltipWithDropdown tooltipText="What are you focusing on to improve your players?" />
            </ManageContentFieldGroup>
          </div>

          <div className={inputContainerClass}>
            <ManageContentFieldGroup
              name="type_of_practice"
              label="Type of Practice (select one)"
              value={content.type_of_practice}
              defaultOption="Select Type of Practice (select one)"
              options={metadataOptions.typeofpractice}
              onChange={onChange}
              error={errors.type_of_practice}
              component={RadioButton}
            />
          </div>

          <div className={inputContainerClass}>
            <ManageContentFieldGroup
              name="stage_of_practice"
              label="Stage of Practice (select one)"
              value={content.stage_of_practice}
              defaultOption="Select Stage of Practice (select one)"
              options={metadataOptions.stageofpractice}
              onChange={onChange}
              error={errors.stage_of_practice}
              component={RadioButton}
            >
              <TooltipWithDropdown tooltipText="Which stage of a session should the practice be coached?" />
            </ManageContentFieldGroup>
          </div>

          <div className={inputContainerClass}>
            <ManageContentFieldGroup
              name="unit_focus"
              label="Unit Focus (select one)"
              value={content.unit_focus}
              defaultOption="Select Unit Focus (select one)"
              options={metadataOptions.unitfocus}
              onChange={onChange}
              error={errors.unit_focus}
              component={RadioButton}
            >
              <TooltipWithDropdown tooltipText="Which unit you will primarily be working with?" />
            </ManageContentFieldGroup>
          </div>

          <div className={inputContainerClass}>
            <ManageContentFieldGroup
              name="area_of_pitch"
              label="Area of Pitch (select one)"
              value={content.area_of_pitch}
              defaultOption="Select Area of Pitch (select one)"
              options={metadataOptions.areaofpitch}
              onChange={onChange}
              error={errors.area_of_pitch}
              component={RadioButton}
            >
              <TooltipWithDropdown
                tooltipText="
                    Where will you set up and coach this practice?<br/><br/>
                    We recommend trying and make the size of the practice realistic to a game situation. For example, if the practice has a Theme of 'Finishing', the Area of the Pitch would be 'Attacking Third'.
                  "
              />
            </ManageContentFieldGroup>
          </div>
        </MaxWidthContainer>
      </div>

      <div className="drill-creation-section">
        <MaxWidthContainer>
          <div className="heading-with-tooltip">
            <h2 className="heading">Set Up Info &amp; Equipment List</h2>
            <TooltipWithDropdown tooltipText="Providing a detailed equipment list will help other coaches easily recreate your practice." />
          </div>

          <div className="row">
            <div className="col-md-4">
              <div className={inputContainerClass}>
                <SelectInput
                  name="time"
                  label="Length of Practice"
                  value={content.time}
                  defaultOption="Select Length of Practice"
                  options={lengthOfPracticeOptions}
                  onChange={onChange}
                  error={errors.time}
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className={inputContainerClass}>
                <TextInput
                  name="area_size"
                  label="Area Size"
                  maxLength={20}
                  value={content.area_size}
                  placeholder="E.g. 20m x 25m"
                  onChange={onChange}
                  error={errors.area_size}
                >
                  <TooltipWithDropdown
                    tooltipText="
                      How big is the area you are working in?<br/><br/>
                      We recommend being specific with your units to help coaches replicate your drill.
                    "
                  />
                </TextInput>
              </div>
            </div>

            <div className="col-md-4">
              <div className={inputContainerClass}>
                <SelectInput
                  name="number_of_players"
                  label="Number of Players"
                  value={content.number_of_players}
                  defaultOption="Select Number of Players"
                  options={numberOfPlayersOptions}
                  onChange={onChange}
                  error={errors.number_of_players}
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className={inputContainerClass}>
                <SelectInput
                  name="balls"
                  label="Number of Balls"
                  value={content.balls}
                  defaultOption="Select Number of Balls"
                  options={numberOfBallsConesOptions}
                  onChange={onChange}
                  error={errors.balls}
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className={inputContainerClass}>
                <SelectInput
                  name="cones"
                  label="Number of Cones"
                  value={content.cones}
                  defaultOption="Select Number of Cones"
                  options={numberOfBallsConesOptions}
                  onChange={onChange}
                  error={errors.cones}
                />
              </div>
            </div>

            <div className="col-md-4">
              <div className={inputContainerClass}>
                <SelectInput
                  name="bibs"
                  label="Number of Bibs"
                  value={content.bibs}
                  defaultOption="Select Number of Bibs"
                  options={numberOfPlayersOptions}
                  onChange={onChange}
                  error={errors.bibs}
                />
              </div>
            </div>
          </div>
        </MaxWidthContainer>
      </div>

      <div className="drill-creation-section">
        <MaxWidthContainer>
          <div className="heading-with-tooltip">
            <h2 className="heading">Additional Information</h2>
            <TooltipWithDropdown tooltipText="Any other information you'd like to add should be inserted here. Use the buttons below to add headings, paragraphs, numbered lists, bullet lists, dropdowns, images and videos." />
            <p className="text-grey">Click and drag the handle icon to re-order content or click the delete button to remove</p>
          </div>

          <div className="main-content-fields">
            <SortableComponent items={mainContentFields} onSortEnd={onSortEnd} />
            <FreeFormButtons addInput={addInput} />
          </div>
        </MaxWidthContainer>
      </div>

    </div>


  );
};

ManageContentForm.propTypes = {
  content: PropTypes.object.isRequired,
  metadataOptions: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onDiagramChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  addInput: PropTypes.func,
  addTextarea: PropTypes.func,
  addAccordionRow: PropTypes.func,
  addListItem: PropTypes.func,
  deleteInput: PropTypes.func,
  onSortEnd: PropTypes.func,
  getContentPage: PropTypes.func,
};

export default ManageContentForm;



// WEBPACK FOOTER //
// ./library/app/components/Create/ManageContentForm.js